import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import FlyerViewer from '../../components/FlyerViewer';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';
import SubmitResourceModal from '../../components/SubmitResourceModal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import LibraryFreeNote from '../../components/LibraryFreeNote';
import { Search, Phone, ExternalLink, MapPin, Baby, Heart, Milk, Car, Shirt, Backpack, Package, Stethoscope, Users, HeartHandshake, Globe, Flag, BadgeCheck } from 'lucide-react';

const CAT_ICON = {
  'pregnancy-prenatal': Heart, 'baby-infant': Baby, 'diaper-assistance': Package,
  'formula-baby-food': Milk, 'car-seats-cribs': Car, 'baby-kid-clothing': Shirt,
  'school-supplies': Backpack, 'childcare': Users, 'prenatal-care': Stethoscope,
  'postpartum-support': HeartHandshake, 'parent-programs': Users,
};

// Soft pastel palette per category (cream/blush/pastel/green). base = unselected, active = selected.
const CAT_STYLE = {
  'pregnancy-prenatal': { base: 'bg-rose-50 text-rose-700 border-rose-200 hover:border-rose-300', active: 'bg-rose-500 text-white border-rose-500 shadow-md', iconBg: 'bg-rose-100 text-rose-600' },
  'baby-infant': { base: 'bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-300', active: 'bg-amber-500 text-white border-amber-500 shadow-md', iconBg: 'bg-amber-100 text-amber-600' },
  'diaper-assistance': { base: 'bg-sky-50 text-sky-700 border-sky-200 hover:border-sky-300', active: 'bg-sky-500 text-white border-sky-500 shadow-md', iconBg: 'bg-sky-100 text-sky-600' },
  'formula-baby-food': { base: 'bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-300', active: 'bg-orange-500 text-white border-orange-500 shadow-md', iconBg: 'bg-orange-100 text-orange-600' },
  'car-seats-cribs': { base: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-300', active: 'bg-emerald-500 text-white border-emerald-500 shadow-md', iconBg: 'bg-emerald-100 text-emerald-600' },
  'baby-kid-clothing': { base: 'bg-violet-50 text-violet-700 border-violet-200 hover:border-violet-300', active: 'bg-violet-500 text-white border-violet-500 shadow-md', iconBg: 'bg-violet-100 text-violet-600' },
  'school-supplies': { base: 'bg-teal-50 text-teal-700 border-teal-200 hover:border-teal-300', active: 'bg-teal-500 text-white border-teal-500 shadow-md', iconBg: 'bg-teal-100 text-teal-600' },
  'childcare': { base: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:border-cyan-300', active: 'bg-cyan-500 text-white border-cyan-500 shadow-md', iconBg: 'bg-cyan-100 text-cyan-600' },
  'prenatal-care': { base: 'bg-pink-50 text-pink-700 border-pink-200 hover:border-pink-300', active: 'bg-pink-500 text-white border-pink-500 shadow-md', iconBg: 'bg-pink-100 text-pink-600' },
  'postpartum-support': { base: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 hover:border-fuchsia-300', active: 'bg-fuchsia-500 text-white border-fuchsia-500 shadow-md', iconBg: 'bg-fuchsia-100 text-fuchsia-600' },
  'parent-programs': { base: 'bg-lime-50 text-lime-700 border-lime-200 hover:border-lime-300', active: 'bg-lime-600 text-white border-lime-600 shadow-md', iconBg: 'bg-lime-100 text-lime-600' },
};
const DEFAULT_STYLE = { base: 'bg-white text-gray-700 border-gray-200 hover:border-primary/40', active: 'bg-primary text-white border-primary shadow-md', iconBg: 'bg-gray-100 text-gray-600' };

const ParentResourceCenter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [submitCats, setSubmitCats] = useState([]);
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [selectedServices, setSelectedServices] = useState([]);
  const [state, setState] = useState(searchParams.get('state') || '');
  const [county, setCounty] = useState(searchParams.get('county') || '');
  const [zip, setZip] = useState(searchParams.get('zip') || '');
  const [q, setQ] = useState(searchParams.get('search') || '');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedRefs, setSavedRefs] = useState(new Set());
  const [reportItem, setReportItem] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);
  const [reportedIds, setReportedIds] = useState(new Set());
  const resultsRef = useRef(null);

  const scrollToResults = () => {
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
  };

  const submitReport = async () => {
    if (!reportItem) return;
    setReporting(true);
    try {
      await axios.post(`${API_URL}/parent-resources/${reportItem.resource_id}/report-link`, { reason: reportReason.trim() });
      setReportedIds((s) => new Set(s).add(reportItem.resource_id));
      toast.success('Thanks! We\u2019ll review this link.');
      setReportItem(null); setReportReason('');
    } catch (e) {
      toast.error(e.response?.status === 401 ? 'Please sign in to report a link' : 'Could not submit report');
    } finally { setReporting(false); }
  };

  const loadMeta = useCallback(async () => {
    try {
      const [m, refs] = await Promise.all([
        axios.get(`${API_URL}/parent-resources/meta`),
        axios.get(`${API_URL}/library/saved-refs?item_type=resource`).catch(() => ({ data: { refs: [] } })),
      ]);
      setMeta(m.data);
      setSavedRefs(new Set(refs.data.refs || []));
      axios.get(`${API_URL}/resources/categories`).then((c) => setSubmitCats(c.data.categories || [])).catch(() => {});
    } catch (e) { console.error('Failed to load parent-resource meta:', e); }
  }, []);

  const search = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (selectedServices.length) params.set('services', selectedServices.join(','));
      if (state) params.set('state', state);
      if (county) params.set('county', county);
      if (zip) params.set('zip', zip);
      if (q) params.set('search', q);
      const r = await axios.get(`${API_URL}/parent-resources?${params.toString()}`);
      setData(r.data);
    } catch (e) { console.error('Parent-resource search failed:', e); toast.error('Could not load resources'); } finally { setBusy(false); }
  }, [category, state, county, zip, q, selectedServices]);

  useEffect(() => {
    document.title = 'National Parent Resource Center | INASW';
    loadMeta();
  }, [loadMeta]);

  useEffect(() => { search(); }, [category, selectedServices]); // eslint-disable-line

  const toggleService = (slug) => {
    setSelectedServices((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);
    scrollToResults();
  };

  const pickCategory = (slug) => {
    setCategory(slug);
    const next = new URLSearchParams(searchParams);
    if (slug) next.set('category', slug); else next.delete('category');
    setSearchParams(next, { replace: true });
    scrollToResults();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF6F0] via-[#FBEEF2] to-[#EFF7F0] pt-20 pb-16" data-testid="parent-resource-center">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Flyer */}
        <div className="max-w-md mx-auto mb-8">
          <FlyerViewer
            src="/flyer-parent-resource-center.jpg"
            alt="National Parent Resource Center — INASW"
            caption="Tap to enlarge, print, or share"
          />
        </div>

        <div className="text-center mb-6">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-primary">National Parent Resource Center™</h1>
          <p className="text-gray-600 mt-2">Find. Share. Support. — verified baby &amp; parent resources, searched Local → County → Statewide → National.</p>
          <div className="max-w-2xl mx-auto mt-4"><LibraryFreeNote variant="banner" /></div>
          <div className="mt-4 flex flex-col items-center gap-1" data-testid="parent-submit-resource">
            <SubmitResourceModal categories={submitCats} triggerClassName="bg-secondary hover:bg-secondary-hover text-white font-semibold" />
            <p className="text-xs text-gray-500">Parents, social workers, nonprofits, churches, clinics &amp; agencies welcome — submissions are reviewed before publishing.</p>
          </div>
        </div>

        {/* Verified-resources reassurance badge (no count number per request) */}
        {meta && (
          <div className="max-w-md mx-auto mb-8">
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg px-6 py-5 flex items-center justify-center gap-4" data-testid="parent-verified-counter">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <BadgeCheck className="w-8 h-8" />
              </div>
              <div className="text-left">
                <div className="text-xl sm:text-2xl font-extrabold leading-tight">Verified Parent &amp; Baby Resources</div>
                <div className="text-sm font-semibold text-emerald-50 mt-1">Searched Local → County → Statewide → National</div>
              </div>
            </div>
          </div>
        )}

        {/* Category buttons — two-column pastel layout, flanked by baby illustrations */}
        <div className="mb-6 relative">
          <img src="/parent-baby-left.jpg" alt="" aria-hidden="true" className="hidden xl:block absolute -left-40 top-16 w-36 h-36 object-cover rounded-full shadow-sm ring-4 ring-white pointer-events-none" />
          <img src="/parent-baby-right.jpg" alt="" aria-hidden="true" className="hidden xl:block absolute -right-40 top-40 w-36 h-36 object-cover rounded-full shadow-sm ring-4 ring-white pointer-events-none" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-primary mb-4 text-center">Browse by Category</h2>
          {/* Mobile-only illustrations */}
          <div className="flex xl:hidden justify-center gap-4 mb-4">
            <img src="/parent-baby-left.jpg" alt="" aria-hidden="true" className="w-16 h-16 object-cover rounded-full shadow-sm ring-2 ring-white" />
            <img src="/parent-baby-right.jpg" alt="" aria-hidden="true" className="w-16 h-16 object-cover rounded-full shadow-sm ring-2 ring-white" />
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-3xl mx-auto">
            <button onClick={() => pickCategory('')}
              className={`col-span-2 sm:col-span-2 px-4 py-3 rounded-xl text-sm font-bold border-2 transition-all flex items-center justify-center gap-2 ${!category ? 'bg-primary text-white border-primary shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-primary/40'}`}
              data-testid="parent-cat-all">
              <Globe className="w-4 h-4" /> All Areas
            </button>
            {(meta?.categories || []).map((c) => {
              const Icon = CAT_ICON[c.slug] || Heart;
              const style = CAT_STYLE[c.slug] || DEFAULT_STYLE;
              const isActive = category === c.slug;
              return (
                <button key={c.slug} onClick={() => pickCategory(c.slug)}
                  className={`px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all flex items-center gap-3 text-left ${isActive ? style.active : style.base}`}
                  data-testid={`parent-cat-${c.slug}`}>
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-white/20 text-white' : style.iconBg}`}>
                    <Icon className="w-4.5 h-4.5" />
                  </span>
                  <span className="flex-1 leading-tight">
                    {c.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Granular service multi-select (e.g. Diapers + Formula + Car Seat + Childcare) */}
        {meta?.services && (
          <div className="bg-white/70 backdrop-blur rounded-xl border border-gray-100 p-3 mb-6 max-w-3xl mx-auto" data-testid="parent-service-filter">
            <p className="text-xs font-semibold text-gray-500 mb-2 text-center">Filter by specific need (pick any):</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {meta.services.map((s) => (
                <button key={s.slug} onClick={() => toggleService(s.slug)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${selectedServices.includes(s.slug) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary/40'}`}
                  data-testid={`parent-service-${s.slug}`}>
                  {s.label}
                </button>
              ))}
              {selectedServices.length > 0 && (
                <button onClick={() => setSelectedServices([])} className="px-2.5 py-1 rounded-full text-xs font-semibold text-red-500 hover:underline" data-testid="parent-service-clear">Clear</button>
              )}
            </div>
          </div>
        )}

        {/* Search bar: State -> County -> ZIP */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm mb-6 grid md:grid-cols-5 gap-3 items-end max-w-4xl mx-auto">
          <div>
            <label className="text-xs text-gray-500">State</label>
            <select value={state} onChange={(e) => setState(e.target.value)} className="w-full h-10 border border-gray-200 rounded-lg px-2 text-sm" data-testid="parent-state">
              <option value="">All states</option>
              {(meta?.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">County</label>
            <Input value={county} onChange={(e) => setCounty(e.target.value)} placeholder="e.g. Travis" className="h-10" data-testid="parent-county" />
          </div>
          <div>
            <label className="text-xs text-gray-500">ZIP</label>
            <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="e.g. 78701" className="h-10" data-testid="parent-zip" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Keyword</label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (search(), scrollToResults())} placeholder="diapers, WIC…" className="h-10" data-testid="parent-keyword" />
          </div>
          <Button onClick={() => { search(); scrollToResults(); }} disabled={busy} className="h-10 bg-primary text-white" data-testid="parent-search-btn">
            <Search className="w-4 h-4 mr-1" /> Search
          </Button>
        </div>

        {/* Results */}
        <div ref={resultsRef} className="scroll-mt-24">
          {data && (
            <p className="text-sm text-gray-500 mb-3" data-testid="parent-results-count">
              {data.total} result{data.total === 1 ? '' : 's'}
              {data.resolved_state ? <> for <span className="font-semibold text-primary">{data.resolved_state}</span></> : ''} — shown Local → County → Statewide → National
            </p>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {(data?.resources || []).map((r) => (
              <div key={r.resource_id} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm" data-testid={`parent-resource-${r.resource_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{r.name}</h3>
                    <span className="inline-block mt-1 text-xs font-semibold text-secondary">{r.parent_category_label}</span>
                  </div>
                  {(() => {
                    const hay = `${r.name} ${r.description || ''} ${(r.tags || []).join(' ')}`.toLowerCase();
                    const homeVisiting = /miechv|home visiting/.test(hay);
                    const national = r.is_national || (r.service_area_type || '').toLowerCase() === 'nationwide';
                    const statewide = !national && (r.service_area_type || '').toLowerCase() === 'statewide';
                    const locator = /locator|find (a )?(local|provider|program|center|clinic)|directory|home visiting|miechv|hotline|helpline|click to find/.test(hay);
                    if (homeVisiting) return <span className="text-[11px] flex items-center gap-1 text-emerald-600 font-semibold flex-shrink-0 text-right"><MapPin className="w-3 h-3" />Statewide Home Visiting Program</span>;
                    if (national) return <span className="text-[11px] flex items-center gap-1 text-blue-600 font-semibold flex-shrink-0 text-right"><Globe className="w-3 h-3" />{locator ? 'Nationwide — Click to Find Local Help' : 'Nationwide'}</span>;
                    if (statewide) return <span className="text-[11px] flex items-center gap-1 text-emerald-600 font-semibold flex-shrink-0 text-right"><MapPin className="w-3 h-3" />{locator ? 'Statewide — Click to Find Local Help' : (r.state || 'Statewide')}</span>;
                    return <span className="text-[11px] flex items-center gap-1 text-emerald-600 font-semibold flex-shrink-0"><MapPin className="w-3 h-3" />{[r.city, r.state].filter(Boolean).join(', ') || 'Local'}</span>;
                  })()}
                </div>
                <p className="text-sm text-gray-600 mt-2">{r.description}</p>
                {(r.eligibility || r.cost) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {r.cost && <span className="mr-2"><strong>Cost:</strong> {r.cost}</span>}
                    {r.eligibility && <span><strong>Eligibility:</strong> {r.eligibility}</span>}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  {r.phone && <a href={`tel:${r.phone}`} className="text-sm text-primary flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{r.phone}</a>}
                  {r.website && (() => {
                    const hay = `${r.name} ${r.description || ''} ${(r.tags || []).join(' ')}`.toLowerCase();
                    const homeVisiting = /miechv|home visiting/.test(hay);
                    const locator = /locator|find (a )?(local|provider|program|center|clinic)|directory|home visiting|miechv|hotline|helpline|click to find/.test(hay);
                    const label = homeVisiting ? 'Find Your Local Provider' : (locator ? 'Click to Find Local Help' : 'Visit');
                    return <a href={r.website} target="_blank" rel="noreferrer" className="text-sm text-primary flex items-center gap-1 font-semibold"><ExternalLink className="w-3.5 h-3.5" />{label}</a>;
                  })()}
                  {r.website && (reportedIds.has(r.resource_id)
                    ? <span className="text-[11px] text-gray-400 flex items-center gap-1"><Flag className="w-3 h-3" />Reported</span>
                    : <button onClick={() => { setReportItem(r); setReportReason(''); }} className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors" data-testid={`report-link-${r.resource_id}`} title="Report a broken or wrong link"><Flag className="w-3 h-3" />Report link</button>)}
                  {r.last_verified && <span className="text-[11px] text-gray-400 ml-auto">Verified {r.last_verified}</span>}
                </div>
                <div className="mt-3">
                  <SaveToLibraryButton
                    item={{ item_type: 'resource', ref_id: r.resource_id, title: r.name, subtitle: r.phone || '', category: r.parent_category_label, url: r.website || '' }}
                    saved={savedRefs.has(r.resource_id)}
                    onSaved={(ref) => setSavedRefs((s) => new Set(s).add(ref))}
                  />
                </div>
              </div>
            ))}
          </div>
          {data && data.total === 0 && (
            <div className="text-center text-gray-500 py-12">No resources found. Try widening your search (remove ZIP/county) — national resources always apply.</div>
          )}
        </div>
      </div>

      <Dialog open={!!reportItem} onOpenChange={(o) => !o && setReportItem(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="report-link-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Flag className="w-4 h-4 text-red-500" />Report a bad link</DialogTitle></DialogHeader>
          {reportItem && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Let us know if <strong>{reportItem.name}</strong>'s link is broken, wrong, or out of date. We'll review it.</p>
              <Textarea rows={3} value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="What's wrong? (optional) e.g. page not found, wrong org, requires login…" data-testid="report-reason-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReportItem(null)}>Cancel</Button>
                <Button onClick={submitReport} disabled={reporting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="report-submit-btn">
                  {reporting ? 'Sending…' : 'Submit report'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentResourceCenter;
